import { SharedValidation } from '@lawyer/shared';
import { z } from 'zod';

/**
 * Who the tab is signed in as, read off an `AuthenticatedSession` answer.
 *
 * A schema rather than a cast, because this is a boundary: the generated types promise
 * `format: uuid` and cannot enforce it, and the two fields end up as branded ids in query keys and
 * in tenant-scoped requests. «A string that came back» is not the same claim as «a user id».
 *
 * It takes the **whole answer**, not two fields picked out of it. Reading `session.user.id` before
 * validating assumes the shape of a body that may not have one — a proxy's error page, a truncated
 * response — and crashes on the assumption instead of reporting «this is not a session». `safeParse`
 * at the call site turns any of that into «no session», which is a state the application already
 * knows what to do with.
 *
 * Deliberately absent from the output: the access token. It goes to
 * `auth-token-storage.util.ts` and appears in no type anything else can hold (CLAUDE.md,
 * «Чувствительность данных»).
 */
export const sessionIdentitySchema = z
  .object({
    user: z.object({
      id: SharedValidation.userIdSchema,
      /**
       * The one readable thing this client knows about the person signed in.
       *
       * The shell puts them on the screen as themselves — a circle with their initials, and their
       * address under it in the account menu (`widgets/app-shell/ui/user-menu.component.tsx`) — and
       * neither can be built from a UUID. A name would be better and does not exist yet: it arrives
       * with the profile screen (STORY-043-05), and until then the address is what answers «am I
       * signed in as myself or as the colleague who used this machine before me».
       *
       * `emailSchema` rather than a bare string, and the same one the server validates with
       * (`packages/shared`): it normalises case and whitespace, so the initials of `Ada@…` and
       * `ada@…` are the same two letters rather than two different circles.
       */
      email: SharedValidation.emailSchema,
    }),
    organization: z.object({
      id: SharedValidation.organizationIdSchema,
      /**
       * The name, kept alongside the id because this product is multi-tenant in the way that
       * matters to a lawyer: the same person works inside several client organizations, and every
       * act, contract and letter on the screen belongs to exactly one of them. The shell names it
       * in the account menu (`widgets/app-shell/ui/user-menu.component.tsx`) so that «whose
       * documents am I looking at» is never a question answered by memory.
       *
       * An id would not do: it is the right thing for a query key and the wrong thing for a human.
       */
      name: z.string().min(1),
    }),
  })
  .transform(({ user, organization }) => ({
    userId: user.id,
    email: user.email,
    organizationId: organization.id,
    organizationName: organization.name,
  }));

export type SessionIdentity = z.output<typeof sessionIdentitySchema>;
